import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';

import { Flex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import zIndex from '@/styles/zIndex';

import { useFeedbackDetailContext } from '../../context/FeedbackDetailContext';
import { FieldBadge } from '../FieldBadge';
import { ValueWrapper } from '../ValueWrapper';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';

export const ScoreField = (
  props: CommonFieldProps & {
    feedback_alias: string;
    rating_score?: number;
    nps_score?: number;
    csat_score?: number;
    ces_score?: number;
  },
) => {
  const changeScore = useFeedbackListStore((state) => state.changeScore);
  const { updateDetail } = useFeedbackDetailContext();

  const onScoreChange = (value: number | null, field_name: string) => {
    changeScore(
      props.feedback_alias,
      value,
      field_name as 'rating_score' | 'nps_score' | 'ces_score' | 'csat_score',
    );
    updateDetail({
      [field_name]: value,
    });
  };

  const allEmpty =
    typeof props.rating_score === 'undefined' &&
    typeof props.nps_score === 'undefined' &&
    typeof props.ces_score === 'undefined' &&
    typeof props.csat_score === 'undefined';

  if (!props.showFull && allEmpty) return null;

  return (
    <PropertyRowWrapper {...props}>
      <Flex g={8}>
        <ScorePopover
          trigger={
            <ValueWrapper>
              <FieldBadge type="Rating" />
              <Typo type="label3" weight="m">
                {props.rating_score ?? '-'}
              </Typo>
            </ValueWrapper>
          }
          value={props.rating_score ?? null}
          field_label="Rating Score"
          onDone={(value) => {
            onScoreChange(value, 'rating_score');
          }}
        />
        <ScorePopover
          trigger={
            <ValueWrapper>
              <FieldBadge type="NPS" />
              <Typo type="label3" weight="m">
                {props.nps_score ?? '-'}
              </Typo>
            </ValueWrapper>
          }
          value={props.nps_score ?? null}
          field_label="NPS Score"
          onDone={(value) => {
            onScoreChange(value, 'nps_score');
          }}
        />
        <ScorePopover
          trigger={
            <ValueWrapper>
              <FieldBadge type="CES" />
              <Typo type="label3" weight="m">
                {props.ces_score ?? '-'}
              </Typo>
            </ValueWrapper>
          }
          value={props.ces_score ?? null}
          field_label="CES Score"
          onDone={(value) => {
            onScoreChange(value, 'ces_score');
          }}
        />
        <ScorePopover
          trigger={
            <ValueWrapper>
              <FieldBadge type="CSAT" />
              <Typo type="label3" weight="m">
                {props.csat_score ?? '-'}
              </Typo>
            </ValueWrapper>
          }
          value={props.csat_score ?? null}
          field_label="CSAT Score"
          onDone={(value) => {
            onScoreChange(value, 'csat_score');
          }}
        />
      </Flex>
    </PropertyRowWrapper>
  );
};

type PopoverProps = {
  value: number | null;
  trigger: React.ReactNode;
  field_label: string;
  onDone: (value: number | null) => void;
};

const ScorePopover = (props: PopoverProps) => {
  return (
    <Popover.Root>
      <Popover.Trigger>{props.trigger}</Popover.Trigger>
      <Popover.Portal>
        <PopupoverContent {...props} />
      </Popover.Portal>
    </Popover.Root>
  );
};

const PopupoverContent = (props: PopoverProps) => {
  const [value, setValue] = useState<number | null>(props.value);
  return (
    <Popover.Content
      side="bottom"
      align="start"
      sideOffset={4}
      className="bg-gray-100 rounded-md p-2.5 border border-gray-200 shadow-md"
      style={{
        zIndex: zIndex.popup,
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Typo type="label3" weight="m">
            {props.field_label}
          </Typo>
          <Popover.Close>
            <Typo
              type="label3"
              weight="m"
              className="text-gray-600 cursor-pointer hover:bg-gray-200 duration-200 transition-all py-1 px-2 rounded-sm"
              onClick={() => {
                props.onDone(null);
              }}
            >
              clear
            </Typo>
          </Popover.Close>
        </div>
        <Input
          sizeType="m"
          value={value ?? undefined}
          onChange={(e) => {
            const value = e.currentTarget.value;
            if (value === '') {
              setValue(null);
              return;
            }
            const newValue = Number(value);
            setValue(newValue);
          }}
          onEnter={(_, e) => e.currentTarget.blur()}
          onBlur={() => props.onDone(value)}
          placeholder="Enter score"
        />
      </div>
      <Popover.Arrow className="fill-gray-100" />
    </Popover.Content>
  );
};
